import {
	Alert,
	Box,
	Container,
	Header,
	SpaceBetween
} from '@cloudscape-design/components'
import { FC, useEffect } from 'react'

import ReadGenericTagsGCP from '../../../ControlledFormInputs/GenericTagsReadGCP'
import ApproverOverview from '../../../ControlledFormInputs/ApproverOverview'
import { buildApplicationTagApprovalPreview } from '../../../../helper/applicationTagApproval'
import { useNewApplicationFormStore } from '../../../../stores/applications/newApplicationFormStore'
import { useGenericTagsManagementFormStore } from '../../../../stores/tags/genericTagsManagementFormStore'

type ApplicationTagsProps = {
	registerValidation?: (validate: () => Promise<boolean>) => void
}

const ApplicationTags: FC<ApplicationTagsProps> = ({ registerValidation }) => {
	const {
		formValues,
		actions: { setFormValues }
	} = useNewApplicationFormStore()
	const {
		actions: { clearFormValues: clearTagFormValues, replaceFormValues }
	} = useGenericTagsManagementFormStore()
	const resolution = formValues.gcpProjectID?.resolution
	const sourceTag = resolution?.selectedProject?.tags
	const inheritedTagsId = resolution?.inheritedTagsID || sourceTag?.tagsID || ''
	const sourceDetails = resolution?.inheritedTagDetails || sourceTag?.details
	let applicationApprovalPreview: any[] = []
	let inheritanceError = ''

	try {
		applicationApprovalPreview = buildApplicationTagApprovalPreview(
			sourceTag?.approvalStatus || [],
			formValues.applicationName
		)
	} catch (error) {
		inheritanceError =
			error instanceof Error
				? error.message
				: 'Selected GCP Project approvals cannot be carried forward.'
	}

	useEffect(() => {
		if (!inheritedTagsId || !sourceDetails) {
			clearTagFormValues()
			setFormValues('inheritGcpTagsBool', false)
			setFormValues('tagsID', '')
			return
		}

		replaceFormValues(sourceDetails)
		setFormValues('inheritGcpTagsBool', true)
		setFormValues('tagsID', inheritedTagsId)
		setFormValues('wbsCode', sourceDetails.WBSCode || '')
		setFormValues('costCenter', sourceDetails.costCenter || '')
		setFormValues('group', sourceDetails.group || '')
	}, [
		clearTagFormValues,
		inheritedTagsId,
		replaceFormValues,
		setFormValues,
		sourceDetails
	])

	useEffect(() => {
		registerValidation?.(async () =>
			Boolean(inheritedTagsId && sourceDetails && !inheritanceError)
		)
	}, [inheritedTagsId, inheritanceError, registerValidation, sourceDetails])

	return (
		<Container header={<Header variant="h2">Resource Tags</Header>}>
			<SpaceBetween direction="vertical" size="l">
				{!inheritedTagsId && (
					<Alert type="warning">
						Select an available GCP Project in Application Details to copy its approved resource tags.
					</Alert>
				)}
				{inheritanceError && (
					<Alert type="error" header="Resource tag approvals unavailable">
						{inheritanceError}
					</Alert>
				)}
				{inheritedTagsId && !inheritanceError && (
					<>
						<ReadGenericTagsGCP
							tagsId={inheritedTagsId}
							showApprovalStatus={false}
						/>
						<div>
							<Box variant="awsui-key-label">{"Application Owner"}</Box>
							{formValues.applicationOwner || '-'}
						</div>
						<ApproverOverview
							approvalStatus={applicationApprovalPreview}
							isDisabled
						/>
					</>
				)}
			</SpaceBetween>
		</Container>
	)
}

export default ApplicationTags
